import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { FileText, ChevronLeft, ChevronRight, Eye, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminPostsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, [page, filter]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getPosts(page, 20, filter);
      setPosts(response.data.posts);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'posted':
        return { color: 'text-[#1E8E3E]', bg: 'bg-[#E6F4EA]', icon: <CheckCircle className="w-4 h-4" /> };
      case 'failed':
        return { color: 'text-[#D93025]', bg: 'bg-[#FCE8E6]', icon: <AlertCircle className="w-4 h-4" /> };
      default:
        return { color: 'text-[#F9AB00]', bg: 'bg-[#FEF7E0]', icon: <Clock className="w-4 h-4" /> };
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Posts Management
          </h1>
          <p className="text-[#5F6368]">View all scheduled and published posts</p>
        </div>

        <div className="flex gap-2">
          {['all', 'pending', 'posted', 'failed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f === 'all' ? null : f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                (f === 'all' && !filter) || filter === f
                  ? 'bg-[#1A73E8] text-white'
                  : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8EAED]'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="dashboard-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-[#DADCE0]" />
            <p className="mt-4 text-[#5F6368]">No posts found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#DADCE0]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Content</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">User ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Auto</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => {
                    const statusConfig = getStatusConfig(post.status);
                    return (
                      <tr key={post.id} className="border-b border-[#DADCE0] hover:bg-[#F8F9FA]">
                        <td className="py-3 px-4 max-w-xs">
                          <p className="text-[#202124] truncate">{post.post_content}</p>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#5F6368]">
                          {post.user_id.slice(0, 8)}...
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 text-xs font-medium bg-[#F1F3F4] text-[#5F6368] rounded">
                            {post.post_type || 'promotional'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${statusConfig.bg} ${statusConfig.color}`}>
                            {statusConfig.icon}
                            {post.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {post.auto_generated ? (
                            <span className="text-xs text-[#1A73E8]">Auto</span>
                          ) : (
                            <span className="text-xs text-[#5F6368]">Manual</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-[#5F6368]">
                          {new Date(post.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-[#DADCE0]">
              <p className="text-sm text-[#5F6368]">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 text-[#5F6368] hover:bg-[#F1F3F4] rounded-lg disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 text-[#5F6368] hover:bg-[#F1F3F4] rounded-lg disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPostsPage;
